import { type AdGroupRow, type AdSetEntry, type GridRow, useAdSets } from '../api';
import { useFormContext, useWatch } from 'react-hook-form';
import { useCallback, useEffect, useMemo } from 'react';

const useAdSetsData = () => {
  const { watch, setValue } = useFormContext();
  const accountId = watch('accountId');
  const selectedCampaigns: string[] = watch('selectedCampaigns');
  const selectedAdGroups: string[] = watch('selectedAdGroups');
  const filterByNameRaw: string[] | undefined = useWatch({ name: 'filterAdSetsByName' });
  const filterByName = useMemo(() => filterByNameRaw ?? [], [filterByNameRaw]);

  const isAdGroupRow = useCallback((row: GridRow): row is AdGroupRow => {
    return 'isAdGroup' in row && row.isAdGroup;
  }, []);

  const { data: adSets, isLoading, isError, error } = useAdSets({
    params: {
      accountId,
      selectedCampaigns,
    },
  });

  const selectedCampaignsWithAdSets = useMemo(() =>
      adSets ? adSets.reduce<AdSetEntry[]>((acc, item): AdSetEntry[] => {
        if (selectedCampaigns.includes(item.campaign_id)) {
          const ad_groups = item.ad_groups.filter(adGroup =>
            selectedAdGroups.includes(adGroup.adgroup_id),
          );
          acc.push({
            ...item,
            ad_groups,
          });
        }

        return acc;
      }, []) : [],
    [adSets, selectedCampaigns, selectedAdGroups]);

  const selectedAdSetsNames =
    useMemo(() => selectedCampaignsWithAdSets.flatMap(campaign =>
      campaign.ad_groups.map(adGroup => adGroup.adgroup_name),
    ), [selectedCampaignsWithAdSets]);


  const allRows: GridRow[] = useMemo(() => {
    if (!adSets) return [];

    const result: GridRow[] = [];

    adSets.forEach((campaign) => {
      result.push(campaign);

      campaign.ad_groups.forEach((adGroup) => {
        result.push({
          adgroup_id: adGroup.adgroup_id,
          adgroup_name: adGroup.adgroup_name,
          campaign_id: campaign.campaign_id,
          campaign_name: campaign.campaign_name,
          account_id: campaign.account_id,
          isAdGroup: true,
        });
      });
    });

    return result;
  }, [adSets]);

  const gridRows: GridRow[] = useMemo(() => {
    if (!allRows.length) return [];

    const previousSelectedAdGroups = selectedAdGroups
      .map(adGroupId => allRows.find(r => isAdGroupRow(r) && r.adgroup_id === adGroupId))
      .filter((row): row is AdGroupRow => row !== undefined);

    const filteredRows = filterByName?.length
      ? allRows.filter(row => {
        if (!isAdGroupRow(row)) return true;

        const adGroupNameLower = row.adgroup_name?.toLowerCase() ?? '';
        return filterByName.every(filter =>
          adGroupNameLower.includes(filter.toLowerCase()),
        );
      })
      : allRows;

    const resultMap = new Map<string, GridRow>();

    filteredRows.forEach(row => {
      const id = isAdGroupRow(row) ? row.adgroup_id : row.campaign_id;
      resultMap.set(id, row);
    });

    previousSelectedAdGroups.forEach(row => {
      if (!resultMap.has(row.adgroup_id)) {
        resultMap.set(row.adgroup_id, row);
      }
    });

    return allRows.filter(row => {
      const id = isAdGroupRow(row) ? row.adgroup_id : row.campaign_id;
      return resultMap.has(id);
    });
  }, [allRows, selectedAdGroups, filterByName, isAdGroupRow]);

  useEffect(() => {
    if (adSets) {
      setValue('$adSetsData', adSets, { shouldValidate: true });
    }
  }, [adSets, setValue]);

  return {
    adSets,
    gridRows,
    selectedCampaignsWithAdSets,
    selectedAdSetsNames,
    isLoading,
    isError,
    error,
    isAdGroupRow,
  };
};

export default useAdSetsData;